import React, { useState, useEffect } from "react";
import MyModal from "components/MyModal";
import * as yup from "yup";
import { useChangePasswordMutation } from "redux/services/identity";
import {
  Alert,
  useToast,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  AlertDescription,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import AlertComponent from "components/Alert";
import { VALIDATIONS } from "utils/validations";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import env from "env";

interface ChangePasswordModalProps {
  visible: boolean;
  onClose: () => void;
}

const schema = yup.object().shape({
  currentPassword: yup.string().required("Current Password is required"),
  newPassword: yup
    .string()
    .matches(VALIDATIONS.PASSWORD.regex, VALIDATIONS.PASSWORD.message)
    .min(8, "Password must be between 8 and 12 characters in length.")
    .max(12, "Password must be between 8 and 12 characters in length.")
    .required("New Password is required"),
  confirmNewPassword: yup
    .string()
    .oneOf(
      [yup.ref("newPassword")],
      "The new password and confirm new password fields doesn’t match"
    )
    .required("Confirm New Password is required"),
});

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  visible,
  onClose,
}) => {
  const toast = useToast();

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const [changePassword, { error, isLoading, isSuccess, isError, reset }] =
    useChangePasswordMutation();

  const [formData, setFormData] = useState<{
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }>({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmNewPassword?: string;
  }>({});

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err: any) {
      const validationErrors: any = {};
      err.inner.forEach((error: any) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();

    if (isValid) {
      changePassword(formData);
    }
  };

  useEffect(() => {
    if (visible) {
      reset();
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (isSuccess) {
      toast({
        title: "Password changed successfully",
        description: "",
        status: "success",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      setTimeout(() => {
        window.location.href = env.api.baseUrl.logoutUrl;
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <MyModal
      isOpen={visible}
      onClose={onClose}
      title="Change Password"
      submitBtnText="Change Password"
      onSubmit={() => handleSubmit()}
      loadingSubmit={isLoading}
      scrollBehavior="inside"
      disabledCloseOutSide
      submitBtnWidth="172px"
    >
      {isError && !!error && (
        <AlertComponent
          status="error"
          mb="12px"
          title={
            (error as any)?.data?.errors
              ?.map((err: any) => err.message)
              ?.join(", ") || "Something went wrong!"
          }
        />
      )}

      <FormControl mb="12px" isInvalid={!!errors.currentPassword}>
        <FormLabel>Current Password</FormLabel>
        <InputGroup>
          <Input
            data-testid="current-password"
            type={showCurrentPassword ? "text" : "password"}
            value={formData.currentPassword}
            name="currentPassword"
            maxLength={12}
            onChange={handleChange}
          />
          <InputRightElement>
            <IconButton
              data-testid="visible-current-password"
              variant="unstyled"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              icon={!showCurrentPassword ? <ViewOffIcon /> : <ViewIcon />}
              aria-label="Toggle Password Visibility"
            />
          </InputRightElement>
        </InputGroup>

        {errors.currentPassword && (
          <FormErrorMessage fontSize={12}>
            {errors.currentPassword}
          </FormErrorMessage>
        )}
      </FormControl>
      <FormControl mb="12px" isInvalid={!!errors.newPassword}>
        <FormLabel>New Password</FormLabel>
        <InputGroup>
          <Input
            data-testid="new-password"
            type={showNewPassword ? "text" : "password"}
            value={formData.newPassword}
            name="newPassword"
            // maxLength={12}
            onChange={handleChange}
          />
          <InputRightElement>
            <IconButton
              data-testid="visible-new-password"
              variant="unstyled"
              onClick={() => setShowNewPassword(!showNewPassword)}
              icon={!showNewPassword ? <ViewOffIcon /> : <ViewIcon />}
              aria-label="Toggle Password Visibility"
            />
          </InputRightElement>
        </InputGroup>
        {errors.newPassword && (
          <FormErrorMessage fontSize={12}>
            {errors.newPassword}
          </FormErrorMessage>
        )}
      </FormControl>
      <FormControl mb="12px" isInvalid={!!errors.confirmNewPassword}>
        <FormLabel>Confirm New Password</FormLabel>
        <InputGroup>
          <Input
            data-testid="confirm-new-password"
            type={showConfirmNewPassword ? "text" : "password"}
            value={formData.confirmNewPassword}
            name="confirmNewPassword"
            // maxLength={12}
            onChange={handleChange}
          />
          <InputRightElement>
            <IconButton
              data-testid="visible-confirm-new-password"
              variant="unstyled"
              onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
              icon={!showConfirmNewPassword ? <ViewOffIcon /> : <ViewIcon />}
              aria-label="Toggle Password Visibility"
            />
          </InputRightElement>
        </InputGroup>

        {errors.confirmNewPassword && (
          <FormErrorMessage fontSize={12}>
            {errors.confirmNewPassword}
          </FormErrorMessage>
        )}
      </FormControl>

      <Alert
        mt="12px"
        backgroundColor="gray.100"
        variant="solid"
        borderRadius="10px"
        alignItems="flex-start"
      >
        <AlertDescription color="black">
          • Password must be between 8 and 12 characters in length.
          <br />• Password must include a combination of (a-z), (A-Z), (0-9),
          (!@#$%&*).
          {/* <br />• Previous passwords are not acceptable. */}
        </AlertDescription>
      </Alert>
    </MyModal>
  );
};

export default ChangePasswordModal;
